import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useT } from '../i18n';
import { USER_TYPES } from '../constants/designer';
import RegisterForm from './RegisterForm';
import { rememberReturnTo, peekReturnTo, clearReturnTo } from '../utils/returnTo';
import './GuestRegisterModal.css';

// ── SIGN UP WITHOUT LEAVING THE PAGE ─────────────────────────────────────────
// A signed-out visitor pressing Like or Message on a public profile. Both need
// an identity — a like document is keyed on the liker, a conversation on its
// two participants — so the account has to exist first.
//
// A MODAL, NOT A REDIRECT TO /login. The visitor was reading this profile; that
// is the whole reason they reached for the button. Sending them to another
// screen throws the page away and asks them to find it again afterwards, and
// "afterwards" is a dashboard they have never seen. The dialog opens over the
// page they are on and closes back onto it.
//
// WHERE THEY LAND, which depends only on what the sign-up still owes:
//   · a designer or a shop filled their entire public profile in on this form,
//     so there is nothing left to ask — the dialog closes and the page they
//     were on re-renders, signed in, with the button they pressed now working;
//   · a customer was never asked for a location, and a Google or phone sign-up
//     was never even asked customer-or-designer. Those go on to finish, and the
//     page they were on rides along in `from` so the last step returns them to
//     it (see AccountType and Settings, which already pass it down that chain).
//
// The destination is also written to localStorage, because that chain crosses
// navigations and an OAuth round trip can replace the page entirely — see
// utils/returnTo.
export default function GuestGateModal({ reason = 'like', name = '', onClose }) {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const here = `${location.pathname}${location.search}`;

  // Written the moment the dialog opens rather than on success: by the time the
  // account exists the sign-up may have navigated away from here more than once.
  useEffect(() => { rememberReturnTo(here); }, [here]);

  // Closed without registering. The stored path goes with it — a destination
  // left behind would redirect them somewhere they never asked to go the next
  // time they do sign up.
  const dismiss = useCallback(() => {
    clearReturnTo();
    onClose();
  }, [onClose]);

  // Escape closes, like every other dismissible overlay in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismiss]);

  function finish(res) {
    const back = peekReturnTo() || here;

    if (res?.isNewAccount) {
      // Google or phone: no account type chosen yet. AccountType consumes the
      // stored path itself, so it is deliberately left in place here.
      navigate('/account-type', { state: { from: { pathname: back } } });
      return;
    }
    clearReturnTo();
    if (res?.userType === USER_TYPES.CUSTOMER) {
      navigate('/settings', { state: { from: { pathname: back } } });
      return;
    }
    // Designer or shop — already complete, and already here.
    onClose();
  }

  const why = reason === 'message' ? t('gate_why_message')
    : reason === 'basket' ? t('gate_why_basket')
      : t('gate_why_like');

  // Portaled to <body>: the trigger sits inside the profile header, and a fixed
  // overlay nested under a transformed or fixed ancestor gets trapped to that
  // ancestor's box rather than the viewport.
  return createPortal(
    <div className="grm-overlay" onClick={dismiss} role="presentation">
      <div
        className="grm-box grm-box--gate"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className="grm-x"
          onClick={dismiss}
          aria-label={t('close_tooltip')}
          title={t('close_tooltip')}
        >✕</button>

        {/* One line saying why this appeared. Without it the dialog reads as a
            sign-up prompt that interrupted for no stated reason. */}
        <p className="grm-why">{why.replace('{name}', name || '')}</p>

        {/* The sign-in link IS kept here, unlike the guest-design modal. Someone
            who already has an account and is merely signed out is a likely
            visitor to a public profile, and a register-only dialog would be a
            dead end for them. It leaves the page, but the destination is in
            localStorage by then and /login reads it back. */}
        <RegisterForm onSuccess={finish} />
      </div>
    </div>,
    document.body
  );
}
